import type { PaymentMethod } from '@/scopes/common/domain/payment-methods/payment-methods'
import * as RadixCollapsible from '@radix-ui/react-collapsible'
import { PaymentMethodIcon } from '@sunwebgroup/uds-ui/payment-method-icon'
import { RadioButton } from '@sunwebgroup/uds-ui/radio-button'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import cn from 'classnames'
import * as React from 'react'
import { useIntl } from '@/intl'
import { SelectableListItem, SelectableRadioButtonGroup } from '@/scopes/checkout/components/SelectableListItem'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { PaymentMethodRowDetails } from '@/scopes/payment/components/PaymentMethods/PaymentMethodRowDetails'
import { PaymentSection } from '@/scopes/payment/components/PaymentSection'
import { getPaymentMethodsPolicyTransformer } from '@/scopes/payment/domain/policies/paymentMethodsAvailability'
import { buildPaymentMethodRows } from '@/scopes/payment/domain/policies/paymentMethodsView'
import { AmountType, PaymentOptions } from '@/scopes/payment/domain/types'
import styles from './PaymentMethods.module.css'

interface PaymentMethodsProps {
  paymentOptions: PaymentOptions
  selectedAmountType: AmountType | ''
  selectedPaymentMethodType: PaymentMethod | null
  customDepositAmount: number | null
  onSelectedPaymentMethodChange: (paymentMethodType: PaymentMethod | null) => void
  onSelectedPaymentMethodUnavailable: (paymentMethodType: PaymentMethod) => void
  onPaymentMethodsUnavailable: (amountType: AmountType) => void
  id?: string
}

function isPaymentMethodAvailableForCustomPartial(
  rowPaymentMethodTypes: PaymentMethod[],
  availablePaymentMethodTypesCustomPartial: PaymentMethod[] | undefined,
) {
  return rowPaymentMethodTypes.some(paymentMethodType =>
    availablePaymentMethodTypesCustomPartial?.includes(paymentMethodType),
  )
}

export function PaymentMethods({
  paymentOptions,
  selectedAmountType,
  selectedPaymentMethodType,
  customDepositAmount,
  onSelectedPaymentMethodChange,
  onSelectedPaymentMethodUnavailable,
  onPaymentMethodsUnavailable,
  id,
}: PaymentMethodsProps) {
  const intl = useIntl()
  const siteName = useConfiguration('siteName')
  const brandName = useConfiguration('brandName')
  const selectedAmount = paymentOptions.paymentAmounts.find(amount => amount.type === selectedAmountType)
  const customDepositAmountIsSelected = customDepositAmount !== null && customDepositAmount !== selectedAmount?.amount

  const paymentMethodRows = React.useMemo(() => {
    if (!selectedAmount) {
      return []
    }

    const formatFeeLabel = (percentageFee: number, fixedFee: number) =>
      intl.formatMessage(
        { id: 'payment_scope_method_fee_label', defaultMessage: '{percentage}% + {fixedFee}' },
        { percentage: percentageFee, fixedFee: intl.formatCurrency(fixedFee) },
      )

    const rows = buildPaymentMethodRows(
      paymentOptions.paymentMethods,
      selectedAmount.availablePaymentMethodTypes,
      intl.formatMessageIfExists,
      formatFeeLabel,
      brandName,
    )
    return getPaymentMethodsPolicyTransformer(siteName)(rows)
  }, [paymentOptions.paymentMethods, selectedAmount, siteName, brandName, intl])

  React.useEffect(() => {
    if (!selectedPaymentMethodType || !customDepositAmountIsSelected || !selectedAmount)
      return

    const selectedRow = paymentMethodRows.find(row => row.key === selectedPaymentMethodType)
    if (!selectedRow)
      return

    const isAvailableForCustomPartial = isPaymentMethodAvailableForCustomPartial(
      selectedRow.paymentMethodTypes,
      selectedAmount.availablePaymentMethodTypesCustomPartial,
    )

    if (!isAvailableForCustomPartial) {
      onSelectedPaymentMethodUnavailable(selectedPaymentMethodType)
    }
  }, [selectedPaymentMethodType, customDepositAmountIsSelected, selectedAmount, paymentMethodRows, onSelectedPaymentMethodUnavailable])

  React.useEffect(() => {
    if (selectedAmount && paymentMethodRows.length === 0) {
      onPaymentMethodsUnavailable(selectedAmount.type)
    }
  }, [onPaymentMethodsUnavailable, paymentMethodRows.length, selectedAmount])

  return (
    <PaymentSection
      title={intl.formatMessage({ id: 'payment_scope_methods_title' })}
      description={intl.formatMessage({ id: 'payment_scope_methods_description' })}
      id={id}
      glyph="PaymentCardWithCoins"
    >
      {!selectedAmount && (
        <Typography className={styles.message}>
          {intl.formatMessage({ id: 'payment_scope_methods_select_amount_message' })}
        </Typography>
      )}

      {selectedAmount && paymentMethodRows.length === 0 && (
        <Typography className={styles.message}>
          {intl.formatMessage({ id: 'payment_scope_methods_unavailable_message' })}
        </Typography>
      )}

      {selectedAmount && paymentMethodRows.length > 0 && (
        <SelectableRadioButtonGroup
          aria-label={intl.formatMessage({ id: 'payment_scope_methods_title' })}
          value={selectedPaymentMethodType ?? ''}
          onValueChange={value => onSelectedPaymentMethodChange(value as PaymentMethod)}
        >
          <div className={styles.rows}>
            {paymentMethodRows.map((row) => {
              const currentPaymentMethodIsDisabled = customDepositAmountIsSelected
                && !isPaymentMethodAvailableForCustomPartial(
                  row.paymentMethodTypes,
                  selectedAmount?.availablePaymentMethodTypesCustomPartial,
                )

              const isSelected = selectedPaymentMethodType === row.key
              const ariaLabel = [row.label, row.secondaryLabel].filter(Boolean).join(' ')

              return (
                <RadixCollapsible.Root key={row.key} open={isSelected}>
                  <SelectableListItem value={row.key} className={styles.row} disabled={currentPaymentMethodIsDisabled}>
                    <div className={cn(styles.rowHeader, { [styles.rowHeaderDisabled]: currentPaymentMethodIsDisabled })}>
                      <RadioButton value={row.key} aria-label={ariaLabel} className={styles.radio} disabled={currentPaymentMethodIsDisabled} onClick={event => event.stopPropagation()} />
                      {row.glyph && <PaymentMethodIcon className={styles.logo} glyph={row.glyph} size={32} aria-hidden />}
                      <div className={styles.labelGroup}>
                        <Typography as="span" variant="body-m-regular-tight" className={styles.label}>
                          {row.label}
                        </Typography>
                        {row.secondaryLabel && (
                          <Typography as="span" variant="body-s-regular-tight" className={styles.secondaryLabel}>
                            {row.secondaryLabel}
                          </Typography>
                        )}
                      </div>
                    </div>
                    <RadixCollapsible.Content className={styles.collapsibleContent}>
                      <div className={styles.details}>
                        <PaymentMethodRowDetails
                          row={row}
                          paymentOptions={paymentOptions}
                          selectedAmountType={selectedAmountType}
                          selectedPaymentMethodType={selectedPaymentMethodType}
                          customDepositAmount={customDepositAmount}
                        />
                      </div>
                    </RadixCollapsible.Content>
                  </SelectableListItem>
                </RadixCollapsible.Root>
              )
            })}
          </div>
        </SelectableRadioButtonGroup>
      )}
    </PaymentSection>
  )
}
